import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import {
  Box, Button, Checkbox, Flex, HStack, Input, Select, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, Badge, useToast,
  Tabs, TabList, Tab, TabPanels, TabPanel, Spinner, IconButton, Tooltip, Image, Progress, Wrap, WrapItem,
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, useBreakpointValue,
} from "@chakra-ui/react"
import { FaFileExcel, FaChevronDown, FaChevronRight, FaCamera, FaTimes, FaEnvelope, FaExclamationTriangle } from "react-icons/fa"

import PageToolbar from "../../components/shared/PageToolbar"
import { Panel } from "../../components/shared/Section"
import ExportMenu from "../../components/shared/ExportMenu"
import { neutral, signal, type } from "../../theme/tokens"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"

const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")

/**
 * Scorecard (Field section, 2026-10-06 — backend docs/michael/rep-field/TASK-scorecard.md).
 * Pick a store: the supplier's card articles with today's SOH and ranging (read live — every crawler /
 * upload shows at once) and whether each SHOULD be ranged there; fill in the in-stock reason,
 * "merchandised as per guide" and a comment, then Submit. History lists every submitted card with its
 * issue counts and an Excel download. Access: masters, or the Scorecard page grant + the supplier.
 */
const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "")

// "S223 Dis-Chem Ballito" for a file name: code + store name, characters Windows refuses in a file name removed.
const storeFilePart = (s) => `${s.site_code} ${s.site_description || ""}`.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim()

const download = async (body, fileName, toast) => {
  try {
    const res = await fetch(`${API}/scorecard/export`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ userid: uid(), ...body }) })
    const ct = res.headers.get("content-type") || ""
    if (!res.ok || !ct.includes("spreadsheetml")) {
      let message = `HTTP ${res.status}`
      try { message = (await res.json()).message || message } catch (e) { /* not json */ }
      throw new Error(message)
    }
    const blob = await res.blob()
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(link.href)
  } catch (err) {
    toast({ title: "Download failed", description: err.message, status: "error", duration: 6000, isClosable: true })
  }
}

// ── Photos per visit (2026-10-12, Helena) ───────────────────────────────────
// Up to MAX_PHOTOS per store visit, from the camera or the gallery. The SERVER normalises every photo
// (any phone format incl. HEIC, auto-rotated, ≤1600 px, JPEG under ~1 MB) — so what the phone sends is
// taken as-is. On a browser that can decode the file we still shrink it first (canvas → JPEG), purely to
// make the upload small on mobile data; if that fails (HEIC on Chrome, odd formats) the original goes up
// and the server does the whole job.
const MAX_PHOTOS = 10
const PRESHRINK_EDGE = 1600
const PRESHRINK_SKIP_UNDER = 600 * 1024

const preShrink = (file) => new Promise((resolve) => {
  if (!file || file.size < PRESHRINK_SKIP_UNDER || !/^image\/(jpeg|png|webp)$/i.test(file.type)) return resolve(file)
  const url = URL.createObjectURL(file)
  const img = new window.Image()
  const done = (out) => { URL.revokeObjectURL(url); resolve(out || file) }
  img.onload = () => {
    try {
      const scale = Math.min(1, PRESHRINK_EDGE / Math.max(img.naturalWidth, img.naturalHeight))
      if (scale >= 1) return done(file)
      const canvas = document.createElement("canvas")
      canvas.width = Math.round(img.naturalWidth * scale)
      canvas.height = Math.round(img.naturalHeight * scale)
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) return done(file)
        done(new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }))
      }, "image/jpeg", 0.8)
    } catch (e) { done(file) }
  }
  img.onerror = () => done(file)
  img.src = url
})

const fmtBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`)

// A stored photo, fetched with the bearer token (never a public URL) and shown from a blob URL.
const usePhotoUrl = (id) => {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let alive = true, obj = null
    if (!id) return undefined
    axios.post(`${API}/scorecard/image`, { userid: uid(), id }, { responseType: "blob" })
      .then((r) => { if (!alive) return; obj = URL.createObjectURL(r.data); setUrl(obj) })
      .catch(() => { if (alive) setUrl("error") })
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj) }
  }, [id])
  return url
}

const StoredPhoto = ({ im, onOpen, size = "96px" }) => {
  const url = usePhotoUrl(im.id)
  return (
    <Box w={size} h={size} borderRadius="8px" overflow="hidden" border="1px solid" borderColor={neutral[200]} bg={neutral[50]}
      cursor={url && url !== "error" ? "zoom-in" : "default"} onClick={() => url && url !== "error" && onOpen(im)} title={im.caption || `Photo ${im.seq}`}
      display="flex" alignItems="center" justifyContent="center">
      {!url ? <Spinner size="xs" /> : url === "error" ? <Text fontSize={type.size.caption} color={neutral[400]}>no photo</Text>
        : <Image src={url} alt={im.caption || `Photo ${im.seq}`} w="100%" h="100%" objectFit="cover" />}
    </Box>
  )
}

const PhotoLightbox = ({ im, onClose }) => {
  const url = usePhotoUrl(im?.id)
  return (
    <Modal isOpen={!!im} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="black">
        <ModalCloseButton color="white" />
        <ModalBody p={2} display="flex" flexDirection="column" alignItems="center">
          {url && url !== "error" ? <Image src={url} alt={im?.caption || ""} maxH="85vh" objectFit="contain" /> : <Spinner color="white" />}
          {im?.caption && <Text color="white" fontSize={type.size.small} mt={2}>{im.caption}</Text>}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const isMasterUser = () => ["1", "2"].includes(String(uid()))

const Scorecard = () => {
  const toast = useToast()
  // The scorecard follows the supplier + retailer in the top bar (Curaden on Dis-Chem).
  const { selectedSupplier, selectedRetailer, setSelectedSupplier, setSelectedRetailer } = useSupplierRetailer()
  const { user_page_access } = usePageAccess()
  const [programs, setPrograms] = useState(null)
  const [error, setError] = useState(null)
  const [stores, setStores] = useState([])
  const [storesVersion, setStoresVersion] = useState(0)

  // Compact (phone) layout — theme/mobile.css `.sc-compact`: on a narrow viewport for everyone, and ALWAYS for a
  // field-only login (Curaden's shared CURADEN: Scorecard is its whole app and it is used on a phone in a store;
  // a big phone or "desktop site" mode reports more than 768 px, which left Helena on the laptop table — 2026-10-13).
  const phone = useBreakpointValue({ base: true, md: false }, { ssr: false }) === true
  const grants = (user_page_access || []).map((p) => p.value)
  const fieldOnly = !isMasterUser() && grants.length > 0 && grants.every((p) => p === "Scorecard" || p === "RepRoute")
  const compact = phone || fieldOnly

  useEffect(() => {
    axios.post(`${API}/scorecard/programs`, { userid: uid() })
      .then((r) => setPrograms(r.data.data || []))
      .catch((e) => setError(e?.response?.data?.message || e.message))
  }, [])

  const program = useMemo(() => (programs || []).find((p) =>
    String(p.suppliercode).toUpperCase() === String(selectedSupplier || "").trim().toUpperCase() &&
    String(p.retailer).toUpperCase() === String(selectedRetailer || "").toUpperCase()) || null, [programs, selectedSupplier, selectedRetailer])
  const base = useMemo(() => (program ? { supplier: program.suppliercode, retailer: program.retailer } : null), [program])

  // One programme and the top bar does not point at it (a field login has no supplier / retailer controls any
  // more — AuthenticatedLayout.hideRail): point it there ourselves instead of showing "select that at the top".
  useEffect(() => {
    if (!programs || program || programs.length !== 1) return
    const p = programs[0]
    if (setSelectedSupplier && String(selectedSupplier || "").trim().toUpperCase() !== String(p.suppliercode).toUpperCase()) setSelectedSupplier(p.suppliercode)
    if (setSelectedRetailer && String(selectedRetailer || "").toUpperCase() !== String(p.retailer).toUpperCase()) setSelectedRetailer(p.retailer)
  }, [programs, program, selectedSupplier, selectedRetailer, setSelectedSupplier, setSelectedRetailer])

  useEffect(() => {
    if (!base) return
    axios.post(`${API}/scorecard/stores`, { userid: uid(), ...base })
      .then((r) => setStores(r.data.data || []))
      .catch((e) => setError(e?.response?.data?.message || e.message))
  }, [base, storesVersion])

  if (error) return <Panel><Text fontSize={type.size.small} color={signal.down}>{error}</Text></Panel>
  if (!programs) return <Panel><Spinner size="sm" /></Panel>
  if (!program) {
    return (
      <Panel>
        <Text fontSize={type.size.body} fontWeight={type.weight.semibold} color={neutral[800]} mb={1}>No scorecard here</Text>
        <Text fontSize={type.size.small} color={neutral[500]}>
          {programs.length ? `The scorecard is for ${programs.map((p) => `${p.label} on ${p.retailer === "DIS" ? "Dis-Chem" : p.retailer}`).join(", ")} — select that at the top.` : "There is no scorecard for your suppliers yet."}
        </Text>
      </Panel>
    )
  }

  return (
    <Box className={`sc-page${compact ? " sc-compact" : ""}`}>
      <PageToolbar
        description={`${program?.label || ""} · ${program?.retailer === "DIS" ? "Dis-Chem" : program?.retailer} store scorecard`}
      />
      <Tabs variant="enclosed" colorScheme="teal" isLazy>
        <TabList>
          <Tab fontSize={type.size.small}>Fill in</Tab>
          <Tab fontSize={type.size.small}>History</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <FillIn base={base} stores={stores} reps={program?.reps || []} onSaved={() => setStoresVersion((v) => v + 1)} toast={toast} />
          </TabPanel>
          <TabPanel px={0}>
            <History base={base} stores={stores} program={program} toast={toast} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}

// ── Fill in ────────────────────────────────────────────────────────────────
const FillIn = ({ base, stores, reps, onSaved, toast }) => {
  const [filter, setFilter] = useState("")
  const [site, setSite] = useState("")
  const [card, setCard] = useState(null)
  const [loading, setLoading] = useState(false)
  const [answers, setAnswers] = useState({}) // article_code -> {reason, merchandised, comment}
  const [note, setNote] = useState("")
  const [saving, setSaving] = useState(false)
  // photos chosen for this visit: [{file, url, caption, size}] — file = what will be uploaded (pre-shrunk when possible)
  const [photos, setPhotos] = useState([])
  const [progress, setProgress] = useState(null) // 0-100 while uploading
  const fileInput = useRef(null)
  const cameraInput = useRef(null)
  // The rep names himself (the Curaden login is shared) — remembered on this device for the next store.
  const [repName, setRepName] = useState(() => { try { return localStorage.getItem("spearbi_scorecard_rep") || "" } catch (e) { return "" } })
  const keepRepName = (v) => { setRepName(v); try { localStorage.setItem("spearbi_scorecard_rep", v) } catch (e) { /* private mode */ } }
  // Curaden (2026-10-13, Helena): the rep picks his name from a list and the card is emailed to him. The list
  // comes from the server (names only); a name remembered from the old typed box counts only if it is on it.
  const pickReps = reps && reps.length > 0
  const repPicked = pickReps ? (reps.find((n) => n.toLowerCase() === repName.trim().toLowerCase()) || "") : repName.trim()

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return f ? stores.filter((s) => `${s.site_code} ${s.site_description} ${s.team_name || ""}`.toLowerCase().includes(f)) : stores
  }, [stores, filter])

  const clearPhotos = useCallback(() => {
    setPhotos((ps) => { ps.forEach((p) => { try { URL.revokeObjectURL(p.url) } catch (e) { /* noop */ } }); return [] })
  }, [])

  const load = useCallback((code) => {
    setSite(code)
    setCard(null)
    setAnswers({})
    setNote("")
    clearPhotos()
    if (!code) return
    setLoading(true)
    axios.post(`${API}/scorecard/card`, { userid: uid(), ...base, site_code: code })
      .then((r) => setCard(r.data.data))
      .catch((e) => toast({ title: "Could not open the store", description: e?.response?.data?.message || e.message, status: "error", isClosable: true }))
      .finally(() => setLoading(false))
  }, [base, toast, clearPhotos])

  const addPhotos = async (list) => {
    const files = Array.from(list || []).filter((f) => f && f.size > 0)
    if (!files.length) return
    const room = MAX_PHOTOS - photos.length
    if (room <= 0) { toast({ title: `At most ${MAX_PHOTOS} photos per store visit`, status: "warning", isClosable: true }); return }
    const take = files.slice(0, room)
    if (take.length < files.length) toast({ title: `Only ${room} more photo${room === 1 ? "" : "s"} fit (max ${MAX_PHOTOS})`, status: "warning", isClosable: true })
    const prepared = []
    for (const f of take) {
      const shrunk = await preShrink(f)
      prepared.push({ file: shrunk, originalSize: f.size, url: URL.createObjectURL(f), caption: "", name: f.name })
    }
    setPhotos((ps) => [...ps, ...prepared])
  }
  const removePhoto = (i) => setPhotos((ps) => { try { URL.revokeObjectURL(ps[i].url) } catch (e) { /* noop */ } return ps.filter((_, j) => j !== i) })
  const setCaption = (i, v) => setPhotos((ps) => ps.map((p, j) => (j === i ? { ...p, caption: v } : p)))

  const set = (code, key, value) => setAnswers((a) => ({ ...a, [code]: { ...(a[code] || {}), [key]: value } }))

  const submit = async () => {
    if (!repPicked) {
      toast({ title: pickReps ? "Choose your name first" : "Type your name first", description: "Rep name at the top of the scorecard.", status: "warning", isClosable: true })
      return
    }
    setSaving(true)
    setProgress(photos.length ? 0 : null)
    try {
      const payload = {
        userid: uid(), ...base, site_code: site, note, rep_name: repPicked,
        answers: Object.entries(answers).map(([article_code, a]) => ({ article_code, ...a })),
        captions: photos.map((p) => p.caption || ""),
      }
      // With photos the card goes up as multipart (the JSON in `payload`, the files in `images`); without, plain JSON as before.
      let r
      if (photos.length) {
        const fd = new FormData()
        fd.append("payload", JSON.stringify(payload))
        photos.forEach((p, i) => fd.append("images", p.file, p.file.name || `photo-${i + 1}.jpg`))
        r = await axios.post(`${API}/scorecard/submit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
          onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : null),
        })
      } else {
        r = await axios.post(`${API}/scorecard/submit`, payload)
      }
      const n = r.data.data.images || 0
      toast({ title: "Scorecard saved", description: `${card.store.site_description} · ${r.data.data.lines} articles${n ? ` · ${n} photo${n === 1 ? "" : "s"}` : ""}${r.data.data.email ? " — emailed" : ""}`, status: "success", isClosable: true })
      onSaved()
      // Back to the store picker, screen cleared (Helena 2026-10-13: "once I click submit, it must clear the
      // screen and go back to the page where I select a store"). The rep name stays (remembered on the device).
      load("")
      setFilter("")
      try { window.scrollTo({ top: 0, behavior: "smooth" }) } catch (err) { /* old browser */ }
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", duration: 8000, isClosable: true })
    } finally {
      setSaving(false)
      setProgress(null)
    }
  }

  // DISC (permanently discontinued) and NEW (no ranging data yet) lines are not issues (2026-10-07)
  // tbc = the retailer is still updating that article's ranging (small grey, never an issue) — 2026-10-07
  const plain = (l) => !["DISC", "NEW", "tbc"].includes(l.ranged_label)
  const issues = card ? {
    notRanged: card.lines.filter((l) => plain(l) && l.should_range === "1" && !l.ranged).length,
    noStock: card.lines.filter((l) => plain(l) && l.ranged && !(Number(l.soh) > 0)).length,
  } : null
  const labelColor = { DISC: "gray.500", NEW: "purple.600" }
  const store = stores.find((s) => s.site_code === site)

  return (
    <Panel>
      <Flex className="sc-repname" gap={2} wrap="wrap" align="center" mb={3} p={3} bg={neutral[50]} border="1px solid" borderColor={neutral[200]} borderRadius="8px">
        <Text fontSize={type.size.small} fontWeight={600} color={neutral[700]}>Rep name</Text>
        {pickReps ? (
          <Select size="sm" maxW="320px" bg={neutral[0]} placeholder="Choose your name" value={repPicked} onChange={(e) => keepRepName(e.target.value)}
            borderColor={repPicked ? neutral[300] : signal.warn}>
            {reps.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        ) : (
          <Input size="sm" maxW="320px" bg={neutral[0]} placeholder="Type your name" value={repName} onChange={(e) => keepRepName(e.target.value)}
            borderColor={repName.trim() ? neutral[300] : signal.warn} />
        )}
        {!repPicked && <Text fontSize={type.size.caption} color={signal.warn}>Needed before you submit{pickReps ? " — the scorecard is emailed to you" : ""}</Text>}
      </Flex>
      <Flex className="sc-filters" gap={2} wrap="wrap" align="center" mb={3}>
        <Input size="sm" maxW="220px" placeholder="Search store / team…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <Select size="sm" maxW="560px" placeholder={`Select a store (${shown.length})`} value={site} onChange={(e) => load(e.target.value)}>
          {shown.map((s) => (
            <option key={s.site_code} value={s.site_code}>
              {s.is_new ? "NEW · " : ""}{s.site_code} · {s.site_description}{s.last_at ? ` · last ${new Date(s.last_at).toLocaleDateString("en-ZA")}` : ""}
            </option>
          ))}
        </Select>
      </Flex>

      {loading && <Spinner size="sm" />}
      {card && (
        <>
          <Flex className="sc-facts" gap={6} wrap="wrap" mb={3} fontSize={type.size.small} color={neutral[600]}>
            <Text><b>Store:</b> {card.store.site_code} · {card.store.site_description}</Text>
            <Text><b>Merchandiser:</b> {card.store.team_name || "–"}</Text>
            {card.store.is_new && <Badge colorScheme="purple" alignSelf="center">New store — not on the scorecard file yet</Badge>}
            {card.store.store_rank && <Text><b>Rank:</b> {card.store.store_rank}</Text>}
            <Text><b>SOH as at:</b> {card.sohAsOf || "–"}</Text>
            {store?.last_at && <Text><b>Last scorecard:</b> {fmtDate(store.last_at)} ({store.last_by})</Text>}
          </Flex>
          <HStack spacing={2} mb={3}>
            <Badge colorScheme={issues.noStock ? "orange" : "green"}>{issues.noStock} ranged, no stock</Badge>
          </HStack>
          <Box className="sc-table-wrap" overflowX="auto" border="1px solid" borderColor={neutral[200]} borderRadius="8px">
            {/* className / data-label on the cells: the phone stylesheet (theme/mobile.css) stacks each row into a card. */}
            <Table className="sc-lines" size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
              <Thead bg={neutral[50]}>
                <Tr>
                  <Th>Article Code</Th><Th>Article Description</Th><Th>Ranged in store</Th><Th isNumeric>SOH</Th>
                  <Th minW="150px">Merch. per guide</Th><Th minW="210px">In-stock</Th><Th minW="200px">Comment</Th>
                </Tr>
              </Thead>
              <Tbody>
                {card.lines.map((l) => {
                  const a = answers[l.article_code] || {}
                  const notRanged = plain(l) && l.should_range === "1" && !l.ranged
                  const noStock = plain(l) && l.ranged && !(Number(l.soh) > 0)
                  return (
                    <Tr key={l.article_code}>
                      <Td className="sc-code" data-label="Code">{l.article_code}</Td>
                      <Td className="sc-desc" data-label="Article">{l.article_description}</Td>
                      <Td className={`sc-ranged${notRanged ? " sc-flag" : ""}`} data-label="Ranged" bg={notRanged ? signal.downSoft : undefined} color={notRanged ? signal.down : undefined} fontWeight={notRanged || (!plain(l) && l.ranged_label !== "tbc") ? 600 : undefined}{...(labelColor[l.ranged_label] ? { color: labelColor[l.ranged_label] } : {})}{...(l.ranged_label === "tbc" ? { fontSize: "0.7rem", color: neutral[400] } : {})}>{l.ranged_label || (l.ranged ? "YES" : "NO")}</Td>
                      <Td className={`sc-soh${noStock ? " sc-flag" : ""}`} data-label="SOH" isNumeric bg={noStock ? signal.warnSoft : undefined} color={noStock ? signal.warn : undefined}>{l.soh == null ? "–" : l.soh}</Td>
                      {/* Tick = YES, untick = NO, never touched = blank (Helena 2026-10-13: "make it a tick box, so if you tick then its YES";
                          column order Ranged · SOH · Merch · In-stock · Comment, her ask the same day). */}
                      {/* No text beside the box (Helena: "remove the word merchandised … taking up too much space") — the
                          column header / the tile's small caption says what it is; an explicit NO shades the cell pink. */}
                      <Td className={`sc-merch${a.merchandised === "NO" ? " sc-flag" : ""}`} data-label="Merch" bg={a.merchandised === "NO" ? signal.downSoft : undefined} title={a.merchandised === "NO" ? "Not merchandised" : a.merchandised === "YES" ? "Merchandised as per guide" : ""}>
                        <Checkbox size="md" colorScheme="teal" aria-label="Merchandised as per guide" isChecked={a.merchandised === "YES"} onChange={(e) => set(l.article_code, "merchandised", e.target.checked ? "YES" : "NO")} />
                      </Td>
                      <Td className="sc-reason" data-label="In-stock">
                        <Select size="xs" placeholder="In-stock reason" value={a.reason || ""} onChange={(e) => set(l.article_code, "reason", e.target.value)}>
                          {card.reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                      </Td>
                      <Td className="sc-comment" data-label="Comment"><Input size="xs" placeholder="Comment" value={a.comment || ""} onChange={(e) => set(l.article_code, "comment", e.target.value)} /></Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </Box>
          <Textarea mt={3} size="sm" placeholder="Note for this visit (optional)" value={note} onChange={(e) => setNote(e.target.value)} />

          <Box mt={3} p={3} bg={neutral[50]} border="1px solid" borderColor={neutral[200]} borderRadius="8px">
            <Flex gap={2} wrap="wrap" align="center" mb={photos.length ? 3 : 0}>
              <Text fontSize={type.size.small} fontWeight={600} color={neutral[700]}>Photos</Text>
              <Text fontSize={type.size.caption} color={neutral[500]}>{photos.length} of {MAX_PHOTOS} — shelf, display, anything worth showing</Text>
              <HStack className="sc-photos-actions" ml="auto" spacing={2}>
                <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { addPhotos(e.target.files); e.target.value = "" }} />
                <input ref={fileInput} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => { addPhotos(e.target.files); e.target.value = "" }} />
                <Button size="sm" leftIcon={<FaCamera />} variant="outline" onClick={() => cameraInput.current?.click()} isDisabled={photos.length >= MAX_PHOTOS || saving}>Take photo</Button>
                <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()} isDisabled={photos.length >= MAX_PHOTOS || saving}>Add from gallery</Button>
              </HStack>
            </Flex>
            {photos.length > 0 && (
              <Wrap spacing={3}>
                {photos.map((p, i) => (
                  <WrapItem key={p.url}>
                    <Box className="sc-photo" w="150px">
                      <Box position="relative" w="150px" h="112px" borderRadius="8px" overflow="hidden" border="1px solid" borderColor={neutral[200]} bg={neutral[100]}>
                        <Image src={p.url} alt={p.name} w="100%" h="100%" objectFit="cover"
                          fallback={<Flex w="100%" h="100%" align="center" justify="center" direction="column"><FaCamera color={neutral[400]} /><Text fontSize={type.size.caption} color={neutral[500]} mt={1}>{p.name}</Text></Flex>} />
                        <IconButton size="xs" aria-label="Remove" icon={<FaTimes />} position="absolute" top="4px" right="4px" colorScheme="blackAlpha" onClick={() => removePhoto(i)} isDisabled={saving} />
                      </Box>
                      <Input size="xs" mt={1} placeholder="Caption (optional)" value={p.caption} onChange={(e) => setCaption(i, e.target.value)} maxLength={200} />
                      <Text fontSize={type.size.caption} color={neutral[400]} mt={0.5}>{fmtBytes(p.file.size)}{p.file.size < p.originalSize ? ` (was ${fmtBytes(p.originalSize)})` : ""}</Text>
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>

          {saving && progress != null && (
            <Box mt={3}>
              <Text fontSize={type.size.caption} color={neutral[500]} mb={1}>{progress < 100 ? `Uploading photos… ${progress}%` : "Saving and emailing…"}</Text>
              <Progress size="sm" colorScheme="teal" value={progress} isIndeterminate={progress >= 100} borderRadius="4px" />
            </Box>
          )}
          <Flex className="sc-submit" justify="flex-end" mt={3}>
            <Button colorScheme="teal" size="sm" onClick={submit} isLoading={saving} loadingText={progress != null && progress < 100 ? `Uploading ${progress}%` : "Saving"}>Submit scorecard</Button>
          </Flex>
        </>
      )}
    </Panel>
  )
}

// ── History ────────────────────────────────────────────────────────────────
const History = ({ base, stores, program, toast }) => {
  const [site, setSite] = useState("")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const [rows, setRows] = useState(null)
  const [open, setOpen] = useState(null) // id
  const [detail, setDetail] = useState({})
  const [exporting, setExporting] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const [resending, setResending] = useState(null)

  const filters = useMemo(() => ({ ...base, site_code: site || undefined, from: from || undefined, to: to || undefined }), [base, site, from, to])

  useEffect(() => {
    setRows(null)
    axios.post(`${API}/scorecard/history`, { userid: uid(), ...filters })
      .then((r) => setRows(r.data.data || []))
      .catch((e) => toast({ title: "Could not load history", description: e?.response?.data?.message || e.message, status: "error", isClosable: true }))
  }, [filters, toast])

  const toggle = (id) => {
    setOpen(open === id ? null : id)
    if (!detail[id]) {
      axios.post(`${API}/scorecard/submission`, { userid: uid(), ...base, id })
        .then((r) => setDetail((d) => ({ ...d, [id]: { lines: r.data.data.lines, images: r.data.data.images || [] } })))
    }
  }

  // Masters: send the email again (after the recipient list changed, or a failed send).
  const resend = async (r) => {
    setResending(r.id)
    try {
      const x = await axios.post(`${API}/scorecard/resend-email`, { userid: uid(), id: r.id })
      toast({ title: "Emailed", description: x.data.message, status: "success", isClosable: true })
      setRows((rs) => rs.map((row) => (row.id === r.id ? { ...row, email_sent_at: new Date().toISOString(), email_error: null } : row)))
    } catch (e) {
      toast({ title: "Email failed", description: e?.response?.data?.message || e.message, status: "error", duration: 8000, isClosable: true })
    } finally {
      setResending(null)
    }
  }

  const label = (program?.label || "Scorecard").replace(/[^A-Za-z0-9 _.-]+/g, "_")
  // One store picked: the file says which store (Helena 2026-10-12: "include the store name, not just the code").
  const picked = site ? stores.find((s) => s.site_code === site) : null
  const exportAll = async () => {
    setExporting(true)
    await download(filters, `Scorecard_${picked ? storeFilePart(picked) : label}_${new Date().toISOString().slice(0, 10)}.xlsx`, toast)
    setExporting(false)
  }

  return (
    <Panel>
      <Flex gap={2} wrap="wrap" align="center" mb={3} justify="space-between">
        <Flex className="sc-hist-filters" gap={2} wrap="wrap" align="center">
          <Select size="sm" maxW="420px" value={site} onChange={(e) => setSite(e.target.value)}>
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.site_code} value={s.site_code}>{s.site_code} · {s.site_description}</option>)}
          </Select>
          <Flex className="sc-hist-date" gap={2} align="center">
            <Text fontSize={type.size.small} color={neutral[500]}>From</Text>
            <Input size="sm" type="date" w="auto" value={from} onChange={(e) => setFrom(e.target.value)} />
            <Text fontSize={type.size.small} color={neutral[500]}>To</Text>
            <Input size="sm" type="date" w="auto" value={to} onChange={(e) => setTo(e.target.value)} />
          </Flex>
        </Flex>
        <Box className="sc-hist-export">
          <ExportMenu isLoading={exporting} width="280px" items={[{ label: "Excel · the scorecards listed", onClick: exportAll, icon: FaFileExcel }]} />
        </Box>
      </Flex>
      {!rows ? <Spinner size="sm" /> : !rows.length ? (
        <Text fontSize={type.size.small} color={neutral[500]}>No scorecards submitted yet.</Text>
      ) : (
        <Box className="sc-table-wrap" overflowX="auto" border="1px solid" borderColor={neutral[200]} borderRadius="8px">
          <Table className="sc-history" size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
            <Thead bg={neutral[50]}>
              <Tr>
                <Th w="28px"></Th><Th>Date</Th><Th>Rep</Th><Th>Store</Th><Th>Merchandiser</Th><Th>Login</Th>
                <Th isNumeric>Should range, not ranged</Th><Th isNumeric>Ranged, no stock</Th><Th isNumeric>Not merchandised</Th><Th>Note</Th><Th isNumeric>Photos</Th><Th>Email</Th><Th w="36px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <HistoryRow key={r.id} r={r} isOpen={open === r.id} detail={detail[r.id]} onToggle={() => toggle(r.id)} onPhoto={setLightbox}
                  onResend={isMasterUser() ? () => resend(r) : null} resending={resending === r.id}
                  onDownload={() => download({ ...base, ids: [r.id] }, `Scorecard_${storeFilePart(r)}_${String(r.submitted_at).slice(0, 10)}.xlsx`, toast)} />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      <PhotoLightbox im={lightbox} onClose={() => setLightbox(null)} />
    </Panel>
  )
}

const EmailCell = ({ r, onResend, resending }) => {
  const sent = r.email_sent_at && !r.email_error
  const label = sent ? `Emailed ${fmtDate(r.email_sent_at)}${r.email_to ? ` to ${r.email_to}` : ""}` : r.email_error ? `Email failed: ${r.email_error}` : "Not emailed yet"
  return (
    <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
      <Tooltip label={label}>
        <Box color={sent ? signal.up : r.email_error ? signal.down : neutral[400]} display="inline-flex">{sent ? <FaEnvelope /> : r.email_error ? <FaExclamationTriangle /> : <FaEnvelope />}</Box>
      </Tooltip>
      {onResend && <Button size="xs" variant="ghost" onClick={onResend} isLoading={resending}>{sent ? "Resend" : "Send"}</Button>}
    </HStack>
  )
}

const HistoryRow = ({ r, isOpen, detail, onToggle, onDownload, onPhoto, onResend, resending }) => (
  <>
    {/* className / data-label on the cells: theme/mobile.css stacks the row into a card on a phone. */}
    <Tr className="sc-hist-row" cursor="pointer" onClick={onToggle} _hover={{ bg: neutral[25] }}>
      <Td className="sc-h-toggle">{isOpen ? <FaChevronDown /> : <FaChevronRight />}</Td>
      <Td className="sc-h-date" data-label="Date" whiteSpace="nowrap">{fmtDate(r.submitted_at)}</Td>
      <Td className="sc-h-rep" data-label="Rep" fontWeight={600}>{r.rep_name || "–"}</Td>
      <Td className="sc-h-store" data-label="Store">{r.site_code} · {r.site_description}</Td>
      <Td className="sc-h-merch" data-label="Merchandiser">{r.team_name || "–"}</Td>
      <Td className="sc-h-login" data-label="Login">{r.submitted_by}</Td>
      <Td className="sc-h-nr" data-label="Should range, not ranged" isNumeric color={r.not_ranged ? signal.down : undefined}>{r.not_ranged}</Td>
      <Td className="sc-h-ns" data-label="Ranged, no stock" isNumeric color={r.no_stock ? signal.warn : undefined}>{r.no_stock}</Td>
      <Td className="sc-h-nm" data-label="Not merchandised" isNumeric color={r.not_merchandised ? signal.down : undefined}>{r.not_merchandised}</Td>
      <Td className="sc-h-note" data-label="Note" maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={r.note || ""}>{r.note}</Td>
      <Td className="sc-h-photos" data-label="Photos" isNumeric>{r.image_count ? <HStack spacing={1} justify="flex-end"><FaCamera /><Text as="span">{r.image_count}</Text></HStack> : ""}</Td>
      <Td className="sc-h-email" data-label="Email"><EmailCell r={r} onResend={onResend} resending={resending} /></Td>
      <Td className="sc-h-dl" onClick={(e) => e.stopPropagation()}>
        <Tooltip label="Download this scorecard"><IconButton size="xs" variant="ghost" icon={<FaFileExcel />} aria-label="Download" onClick={onDownload} /></Tooltip>
      </Td>
    </Tr>
    {isOpen && (
      <Tr className="sc-hist-detail">
        <Td colSpan={13} bg={neutral[25]} p={2}>
          {!detail ? <Spinner size="xs" /> : (
            <>
            {detail.images.length > 0 && (
              <Wrap spacing={2} mb={3}>
                {detail.images.map((im) => <WrapItem key={im.id}><StoredPhoto im={im} onOpen={onPhoto} /></WrapItem>)}
              </Wrap>
            )}
            <Table className="sc-detail" size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1 } }}>
              <Thead><Tr><Th>Article</Th><Th>Ranged</Th><Th isNumeric>SOH</Th><Th>In-stock</Th><Th>Merchandised</Th><Th>Comment</Th></Tr></Thead>
              <Tbody>
                {detail.lines.map((l) => {
                  const special = ["DISC", "NEW", "tbc"].includes(l.ranged_label)
                  const notRanged = !special && l.should_range === "1" && !l.ranged
                  const noStock = !special && l.ranged && !(Number(l.soh) > 0)
                  return (
                    <Tr key={l.article_code}>
                      <Td className="sc-d-art" data-label="Article">{l.article_code} · {l.article_description}</Td>
                      <Td className="sc-d-ranged" data-label="Ranged" color={notRanged ? signal.down : undefined}>{l.ranged_label || (l.ranged ? "YES" : "NO")}</Td>
                      <Td className="sc-d-soh" data-label="SOH" isNumeric color={noStock ? signal.warn : undefined}>{l.soh == null ? "–" : Number(l.soh)}</Td>
                      <Td className="sc-d-reason" data-label="In-stock">{l.reason || ""}</Td>
                      <Td className="sc-d-merch" data-label="Merchandised" color={l.merchandised === "NO" ? signal.down : undefined}>{l.merchandised || ""}</Td>
                      <Td className="sc-d-comment" data-label="Comment">{l.comment || ""}</Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
            </>
          )}
        </Td>
      </Tr>
    )}
  </>
)

export default Scorecard
